// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Navbar, Nav } from "react-bootstrap";
import { FaBell, FaBars, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import pageBackIcon from "../assets/page_back.svg";
import "../css/Notifications.css";
import "../css/Navbar.css";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllAsRead,
  setSelectedCategory,
} from "../features/notifications/notificationSlice";
import { getNotificationNavigationPath } from "../firebase/firebase";
import { ShimmerNotifications } from "./Shimmer";

export default function Topbar({ onMenuClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedDriver } = useSelector((state) => state.driver);
  const { currentBase } = useSelector((state) => state.serviceLocations);
  const {
    notifications,
    unreadCount,
    unreadByCategory,
    isLoading,
    selectedCategory,
  } = useSelector((state) => state.notifications);

  const [showNotifications, setShowNotifications] = useState(false);

  const tabs = [
    { id: "ALL",      label: "All"      },
    { id: "UPDATES",  label: "Updates"  },
    { id: "SERVICES", label: "Services" },
    { id: "DRIVERS",  label: "Drivers"  },
    { id: "OTHERS",   label: "Others"   },
  ];

  useEffect(() => {
    if (showNotifications) {
      dispatch(fetchNotifications({ category: selectedCategory }));
    }
  }, [showNotifications, selectedCategory, dispatch]);

  const handleTabChange = (tabId) => {
    dispatch(setSelectedCategory(tabId));
    dispatch(fetchNotifications({ category: tabId }));
  };

  const handleNotificationClick = (notification) => {
    if (!notification.IsRead) {
      dispatch(markNotificationAsRead(notification.Id));
    }
    const path = getNotificationNavigationPath(notification);
    handleCloseNotifications();
    navigate(path);
  };

  const handleMarkAllAsRead = () => {
    const category = selectedCategory === "ALL" ? null : selectedCategory;
    dispatch(markAllAsRead(category));
  };

  const getPageTitle = () => {
    const path = location.pathname;

    if (/^\/service-location\/[^/]+\/service\/[^/]+$/.test(path)) return "Service Settings";
    if (/^\/service-location\/[^/]+$/.test(path)) return "Service Locations";

    const titleMap = {
      "/":                 "Dashboard",
      "/dashboard":        "Dashboard",
      "/driver":           "Drivers",
      "/driver-details":   "Driver Details",
      "/edit-driver":      "Edit Driver",
      "/invite-driver":    "Invite Driver",
      "/services":         "Services",
      "/bookings":         "Bookings",
      "/profile":          "Profile",
      "/profile-empty":    "Profile",
      "/profile-settings": "Profile Settings",
      "/support":          "Support",
      "/reviews":          "Reviews",
      "/earnings":         "Earnings",
      "/payouts":          "Payouts",
      "/business-profile": "Business Profile",
      "/signup":           "Sign Up",
      "/signin":           "Sign In",
      "/login":            "Login",
      "/create-password":  "Create Password",
      "/forgot-password":  "Forgot Password",
      "/new-password":     "New Password",
      "/availability":     "Availability",
      "/service-area":     "Service Area",
      "/terms-policies":   "Terms & Policies",
      "/flash-sales":      "Flash Sales",
      "/service-settings": "Service Settings",
      "/service-location": "Service Locations",
    };

    return titleMap[path] || "Dashboard";
  };

  const isBookingDetails = location.pathname.startsWith("/booking-details/");
  const bookingId = isBookingDetails ? location.pathname.split("/").pop() : null;

  const showBackButton =
    ["/driver-details", "/edit-driver", "/invite-driver"].includes(location.pathname) ||
    isBookingDetails ||
    /^\/service-location\/.+/.test(location.pathname);

  const getDisplayTitle = () => {
    const path = location.pathname;

    if (location.pathname === "/driver-details" && selectedDriver) {
      return selectedDriver.FullName || selectedDriver.name || "Driver Details";
    }

    if (isBookingDetails && bookingId) {
      return `Order - id: #${bookingId}`;
    }

    // /service-location/:baseId/service/:serviceId
    const serviceMatch = path.match(/^\/service-location\/[^/]+\/service\/([^/]+)$/);
    if (serviceMatch) {
      const serviceId   = serviceMatch[1];
      const baseName    = currentBase?.BaseName || "Location";
      const service     = currentBase?.Services?.find(
        (s) => String(s.ServiceId) === String(serviceId)
      );
      const serviceName = service?.ServiceName || "Service Settings";
      return `Service Locations › ${baseName} › ${serviceName}`;
    }

    // /service-location/:baseId
    if (/^\/service-location\/[^/]+$/.test(path)) {
      return `Service Locations › ${currentBase?.BaseName || "Location Details"}`;
    }

    return getPageTitle();
  };

  const handleOpenNotifications = () => {
    handleMarkAllAsRead();
    setShowNotifications(true);
    document.body.style.overflow = "hidden";
  };

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    document.body.style.overflow = "unset";
  };

  const getNotificationIcon = (type) => {
    const typeUpper = (type || "").toUpperCase();
    switch (typeUpper) {
      case "NEW_BOOKING_REQUEST":
      case "NEW_BOOKING":            return "📦";
      case "DRIVER_ON_THE_WAY":      return "🚗";
      case "DRIVER_ARRIVED":         return "📍";
      case "SERVICE_STARTED":        return "🔧";
      case "JOB_COMPLETED":          return "✅";
      case "CUSTOMER_RATING":
      case "NEW_REVIEW":             return "⭐";
      case "BOOKING_CANCELLED":      return "❌";
      case "DRIVER_SIGNUP_COMPLETE":
      case "DRIVER_INVITED":         return "👤";
      case "PAYOUT":
      case "PAYOUT_RELEASED":        return "💰";
      default:                       return "🔔";
    }
  };

  return (
    <>
      {/* ── Navbar — height + padding controlled by Navbar.css ── */}
      <Navbar bg="white" className="border-bottom">

        <Navbar.Brand className="topbar-brand">
          {/* Hamburger — visible on mobile via CSS */}
          <button className="mobile-menu-btn" onClick={onMenuClick}>
            <FaBars size={22} color="#333" />
          </button>

          {/* Back button */}
          {showBackButton && (
            <img
              src={pageBackIcon}
              alt="Back"
              className="topbar-back-btn"
              onClick={() => navigate(-1)}
              title="Go Back"
            />
          )}

          {/* Page title — 2 lines on mobile, 1 line on desktop */}
          <span className="topbar-title" title={getDisplayTitle()}>
            {getDisplayTitle()}
          </span>
        </Navbar.Brand>

        <Nav className="ms-auto d-flex align-items-center">
          <div
            onClick={handleOpenNotifications}
            style={{
              borderRadius: "4px",
              border: "1px solid #E1E6F1",
              backgroundColor: "#F6F6FF",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              position: "relative",
            }}
          >
            <FaBell size={20} color="#1C1C1C" />
            {unreadCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  backgroundColor: "#EF4444",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: 600,
                  borderRadius: "50%",
                  minWidth: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 4px",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
        </Nav>
      </Navbar>

      {/* ── Notifications Slide Panel ── */}
      {showNotifications && (
        <>
          <div
            className="notifications-overlay"
            onClick={handleCloseNotifications}
          />
          <div className="notifications-panel">

            {/* Header */}
            <div className="notifications-header">
              <h2 className="notifications-title">Notifications</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  className="notifications-close"
                  onClick={handleCloseNotifications}
                >
                  <FaTimes size={16} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="notifications-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`notification-tab ${selectedCategory === tab.id ? "active" : ""}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="tab-badge">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* List */}
            <div className="notifications-list">
              {isLoading ? (
                <ShimmerNotifications count={5} />
              ) : notifications.length === 0 ? (
                <div className="no-notifications">
                  <p>No notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.Id}
                    className={`notification-card ${!notification.IsRead ? "unread" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-content">
                      <div className="notification-title-row">
                        {!notification.IsRead && (
                          <span className="unread-dot" />
                        )}
                        <h4 className="notification-card-title">
                          {notification.Title}
                        </h4>
                      </div>
                      <p className="notification-message">
                        {notification.Body}
                      </p>
                      <span className="notification-time">
                        {notification.TimeAgo}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}